import { ChangeEvent, useCallback, useState } from 'react';
import FormField from './FormField';
import {
  Button,
  Container,
  ErrorMessage,
  Heading,
  ResultItem,
  ResultsContainer,
  SecondaryButton,
  Select,
  SmallSecondaryButton,
  Text,
  TextStrong
} from './PassengerTools.styles';
import { copyToClipboard } from './utils/createTestUser';
import {
  InventoryType,
  InventorySearchResult,
  findInventoryVehicles,
  isZipCodeInventoryType
} from './utils/findInventoryType';

const inventoryTypeOptions: Array<{
  value: InventoryType;
  label: string;
  description: string;
}> = [
  {
    value: 'WIP',
    label: 'WIP',
    description: 'Work In Progress inventory vehicles'
  },
  {
    value: 'DMP',
    label: 'DMP',
    description: 'Dealer Marketplace inventory vehicles'
  },
  {
    value: 'PIP',
    label: 'PIP',
    description: 'Purchase in Progress inventory vehicles'
  }
];

export default function Vehicles() {
  const [selectedInventoryType, setSelectedInventoryType] =
    useState<InventoryType>('WIP');
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchResults, setSearchResults] = useState<InventorySearchResult[]>(
    []
  );

  const handleSearch = useCallback(async () => {
    setIsSearching(true);
    setErrorMessage('');

    try {
      const searchResult = await findInventoryVehicles({
        inventoryType: selectedInventoryType
      });
      setSearchResults(prev => [...prev, searchResult]);
    } catch (error) {
      console.error('Inventory search error:', error);
      setErrorMessage(
        `Failed to search ${selectedInventoryType} inventory: ${error}`
      );
    } finally {
      setIsSearching(false);
    }
  }, [selectedInventoryType]);

  if (searchResults.length > 0) {
    return (
      <Container>
        <Heading>Vehicle Inventory Search Results</Heading>

        <ResultsContainer>
          {searchResults.map((result, index) => (
            <ResultItem key={index}>
              <Text className="mb-sm">
                <TextStrong>Inventory Type:</TextStrong> {result.inventoryType}
              </Text>

              <Text className="mb-sm">
                <TextStrong>Total Results:</TextStrong>{' '}
                {result.totalResults.toLocaleString()}
              </Text>

              <Text className="mb-sm">
                <TextStrong>Searched At:</TextStrong>{' '}
                {new Date(result.searchedAt).toLocaleString()}
              </Text>

              {!isZipCodeInventoryType(result.inventoryType) &&
                result.vehicles.length > 0 && (
                  <div>
                    <Text className="mb-sm text-white">
                      <strong>Sample Vehicles (first 20):</strong>
                    </Text>
                    <div className="space-y-sm">
                      {result.vehicles
                        .slice(0, 20)
                        .map((vehicle, vehicleIndex) => (
                          <div
                            key={vehicleIndex}
                            className="bg-gray-800/50 flex items-center gap-sm rounded-xs p-sm text-gray-300 t-body-xs"
                          >
                            <span className="flex-1">
                              <strong>Vehicle ID:</strong> {vehicle.vehicleId}
                            </span>
                            <span className="flex-1">
                              <strong>Lock Type:</strong> {vehicle.lockType}
                            </span>
                            <div className="flex gap-xs">
                              <a
                                href={`/vehicle/${vehicle.vehicleId}`}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-md bg-blue-600 px-sm py-xs text-xs text-white hover:bg-blue-700"
                                data-analytics-id={null}
                                title="View vehicle page"
                                data-testid="view-vehicle"
                              >
                                View
                              </a>
                              <SmallSecondaryButton
                                onClick={() =>
                                  copyToClipboard(vehicle.vehicleId.toString())
                                }
                                data-analytics-id={null}
                                title="Copy vehicle ID"
                                data-testid="copy-vehicle-id"
                              >
                                Copy ID
                              </SmallSecondaryButton>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
            </ResultItem>
          ))}
        </ResultsContainer>

        <div className="flex justify-end pt-md">
          <SecondaryButton
            type="button"
            onClick={() => setSearchResults([])}
            data-analytics-id={null}
            data-testid="clear-results"
          >
            Clear
          </SecondaryButton>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Heading>Find Vehicles</Heading>

      <FormField
        label="Inventory Type"
        htmlFor="inventory-type"
        helperText={
          inventoryTypeOptions.find(opt => opt.value === selectedInventoryType)
            ?.description
        }
      >
        <Select
          id="inventory-type"
          value={selectedInventoryType}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            setSelectedInventoryType(e.target.value as InventoryType)
          }
        >
          {inventoryTypeOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </FormField>

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      <div className="flex justify-end pt-md">
        <Button
          type="button"
          onClick={handleSearch}
          data-analytics-id={null}
          disabled={isSearching}
          data-testid="find-vehicles"
        >
          {isSearching ? 'Searching...' : 'Find Vehicles'}
        </Button>
      </div>
    </Container>
  );
}
